<template>
  <div class="card rounded-3 congestion-card">
    <div class="card-header d-flex justify-content-between align-items-center">
      <h6 class="fs-16 m-0 p-0">{{ headerText }}</h6>

      <div class="d-flex gap-1 align-items-center">
        <b-button
          size="sm"
          @click="() => change('all')"
          :variant="getVariant('all')"
          >{{ $t("congestion.all") }}</b-button
        >
        <b-button
          v-for="btn in buttonConfigs"
          :key="btn.key"
          size="sm"
          @click="() => change(btn.key)"
          :variant="getVariant(btn.key)"
        >
          {{ btn.label }}
        </b-button>
      </div>
    </div>
    <div class="card-body" :style="`height: ${state.fixedHeight}px;`">
      <apexchart
        v-if="state.showChart"
        class="apex-charts"
        :height="state.chartHeight"
        width="100%"
        dir="ltr"
        :options="state.doughnutChart.chartOptions"
        :series="state.doughnutChart.series"
        ref="apexchartRef"
      />
    </div>
  </div>
</template>

<script>
import { isEqual, keys, round, sum, toPairsIn } from "lodash";
import { computed, onMounted, onUnmounted, reactive, ref, watch } from "vue";
import i18n from "@/i18n";
import { sensorLogsService } from "@/services";
import { useStore } from "vuex";
import { useRoute } from "vue-router";
import { getFacilitySelected } from "@/utils/facility-account";
export default {
  props: {
    isYesterday: {
      type: Boolean,
      default: false,
    },
    headerText: {
      type: String,
      default: i18n.global.t("congestion.yesterday"),
    },
    facilityId: {
      type: String,
      default: "",
    },
    fullscreen: {
      type: Boolean,
      default: false,
    },
    is2K: {
      type: Boolean,
      default: false,
    },
  },
  setup(props) {
    const facilityInfo = ref(null);
    const initCongestionDefault = {
      bluePart: {
        percent: null,
        totalPeopleCount: 0,
      },
      yellowPart: {
        percent: null,
        totalPeopleCount: 0,
      },
      redPart: {
        percent: null,
        totalPeopleCount: 0,
      },
      grayPart: {
        percent: null,
        totalPeopleCount: 0,
      },
    };

    const i18nMapLabel = {
      bluePart: "congestion.morning",
      yellowPart: "congestion.daytime",
      redPart: "congestion.night",
      grayPart: "congestion.other",
    };

    const i18nMapLabelForRoom = {
      bluePart: "congestion.morningRoom",
      yellowPart: "congestion.daytimeRoom",
      redPart: "congestion.nightRoom",
      grayPart: "congestion.otherRoom",
    };

    const timeMapper = {
      bluePart: "05:00-09:00",
      yellowPart: "09:00-17:00",
      redPart: "17:00-24:00",
      grayPart: "24:00-05:00",
    };

    const timeMapperForRoom = {
      grayPart: "00:00-05:00",
      bluePart: "05:00-09:00",
      yellowPart: "09:00-17:00",
      redPart: "17:00-24:00",
    };

    const dataPointIndexRef = ref(-1);

    const defaultChartOption = {
      chartOptions: {
        tooltip: {
          x: {
            show: true,
            formatter: (seriesName) => {
              let key = "";
              const facilityType = facilityInfo.value?.type || "SAUNA";
              const isRoom =
                facilityType === "ROOM" || facilityType === "ROOM_1";

              if (seriesName.includes(timeMapper.bluePart)) {
                key = isRoom
                  ? "congestion.morningUseJal"
                  : "congestion.morningUse";
              } else if (seriesName.includes(timeMapper.yellowPart)) {
                key = isRoom ? "congestion.dayUseJal" : "congestion.dayUse";
              } else if (seriesName.includes(timeMapper.redPart)) {
                key = isRoom ? "congestion.nightUseJal" : "congestion.nightUse";
              } else {
                key = isRoom
                  ? "congestion.otherUseRoomJal"
                  : "congestion.otherUse";
              }
              return i18n.global.t(key);
            },
          },
          y: {
            formatter: (value, { config, seriesIndex }) => {
              const total = formatter(
                config.mappers[seriesIndex].totalPeopleCount
              );
              return `${total}${i18n.global.t("congestion.person")}`;
            },
            title: {
              formatter: (seriesName) => {
                let key = "";
                const facilityType = facilityInfo.value?.type || "SAUNA";
                const isRoom =
                  facilityType === "ROOM" || facilityType === "ROOM_1";

                if (seriesName.includes(timeMapper.bluePart)) {
                  key = isRoom
                    ? "congestion.morningUseJal"
                    : "congestion.morningUse";
                } else if (seriesName.includes(timeMapper.yellowPart)) {
                  key = isRoom ? "congestion.dayUseJal" : "congestion.dayUse";
                } else if (seriesName.includes(timeMapper.redPart)) {
                  key = isRoom
                    ? "congestion.nightUseJal"
                    : "congestion.nightUse";
                } else {
                  key = isRoom
                    ? "congestion.otherUseJal"
                    : "congestion.otherUse";
                }
                return i18n.global.t(key);
              },
            },
          },
        },
        mappers: [],
        chart: {
          type: "donut",
          redrawOnParentResize: true,
          redrawOnWindowResize: true,
          events: {
            dataPointMouseEnter: (e, d, { dataPointIndex }) => {
              dataPointIndexRef.value = dataPointIndex;
            },
            dataPointMouseLeave: () => {
              dataPointIndexRef.value = -1;
            },
          },
        },
        labels: [],
        dataLabels: {
          enabled: true,
          dropShadow: {
            enabled: false,
          },
          style: {
            fontSize: "10px",
          },
        },
        noData: {
          text: i18n.global.t("t-no-data"),
        },
        legend: {
          position: "bottom",
          customLegendItems: [],
          fontSize: "10px",
        },
        colors: ["#6691E7", "#E8BC52", "#ED5E5E", "#A1A1A1"],
        plotOptions: {
          pie: {
            donut: {
              labels: {
                show: true,
                name: {
                  show: true,
                  fontSize: "10px",
                  color: "#A1A1A1",
                  formatter: () => {
                    return i18n.global.t("congestion.totalInfo");
                  },
                },
                value: {
                  show: true,
                  fontSize: "12px",
                  formatter: (value, { config }) => {
                    const total = formatter(
                      sum(config.mappers.map((m) => m.totalPeopleCount))
                    );
                    return `${total}${i18n.global.t("congestion.person")} `;
                  },
                },
                total: {
                  show: true,
                  fontSize: "12px",
                  color: "#A1A1A1",
                  label: i18n.global.t("congestion.totalInfo"),
                  formatter: ({ config }) => {
                    const total = formatter(
                      sum(config.mappers.map((m) => m.totalPeopleCount))
                    );
                    return `${total}${i18n.global.t("congestion.person")} `;
                  },
                },
              },
            },
          },
        },
      },
      series: [],
    };

    const state = reactive({
      chartData: {
        ...initCongestionDefault,
      },
      doughnutChart: { ...defaultChartOption },
      filterType: "all",
      hasValue: false,
      fixedHeight: 311,
      chartHeight: 280,
      showChart: true,
    });

    const store = useStore();
    const route = useRoute();
    const refreshData = computed(
      () => store.getters["settings/getRefreshData"]
    );
    const secondRefresh = computed(() => store.getters["settings/getSeconds"]);
    const intervalRefreshData = ref();
    const apexchartRef = ref(null);
    let resizeTimeout = null;

    const handleResize = () => {
      if (resizeTimeout) {
        clearTimeout(resizeTimeout);
      }
      resizeTimeout = setTimeout(() => {
        state.showChart = false;
        setTimeout(() => {
          state.showChart = true;
        }, 50);
      }, 500);
    };

    const getVariant = (type = "all") => {
      if (isEqual(state.filterType, type)) {
        return "primary";
      } else {
        return "light";
      }
    };

    const change = (type = "all") => {
      state.filterType = type;
    };

    const formatter = (quantity) =>
      new Intl.NumberFormat("en-US").format(quantity);

    const getLegendsAndSeries = (data, labels, facilityType) => {
      const currentTimeMapper =
        facilityType === "ROOM" || facilityType === "ROOM_1"
          ? timeMapperForRoom
          : timeMapper;

      const lKeys = keys(data);
      const legends = lKeys.map(
        (l) => `${i18n.global.t(labels[l])}\n${currentTimeMapper[l]}`
      );

      const mappers = toPairsIn(data).map((pair) => ({
        time: currentTimeMapper[pair[0]],
        name: i18n.global.t(labels[pair[0]]),
        value: round(pair[1].percent || 0, 1),
        totalPeopleCount: pair[1].totalPeopleCount,
      }));

      const series = mappers.map((m) => m.value);
      const hasValue = series.some((s) => !isEqual(s, 0));

      return {
        legends,
        series: hasValue ? series : [],
        mappers,
        hasValue,
      };
    };

    const ROOMTYPE = {
      all: null,
      saunaMen: "SAUNA_ROOM_MALE",
      saunaWomen: "SAUNA_ROOM_FEMALE",
      room1: "ROOM_1",
      room2: "ROOM_2",
    };

    const getRoomType = (filterType) => {
      return ROOMTYPE[filterType];
    };

    const getCongestionSituation = async () => {
      let facilityType = "SAUNA";
      if (facilityInfo.value !== null) {
        facilityType = facilityInfo.value.type || "SAUNA";
      }
      if (!props.facilityId) return;
      const params = {
        facilityId: props.facilityId,
        isDay: props.isYesterday ? true : false,
        roomType: getRoomType(state.filterType),
      };
      const response = await sensorLogsService.getCongestionSituationDashboard(
        params
      );
      if (response) {
        state.chartData = response;
        const labels =
          facilityType === "SAUNA" ? i18nMapLabel : i18nMapLabelForRoom;
        const { legends, series, mappers, hasValue } = getLegendsAndSeries(
          state.chartData,
          labels,
          facilityType
        );
        const legend = {
          ...state.doughnutChart.chartOptions.legend,
          customLegendItems: legends,
          show: hasValue,
        };

        state.hasValue = hasValue;
        const height = hasValue ? (props.is2K ? 478 : 320) : (props.is2K ? 435 : 280);
        const fullscreen = props.fullscreen;
        state.chartHeight = fullscreen ? height + 60 : height;
        state.fixedHeight = fullscreen ? (props.is2K ? 460 : 311) + 60 : (props.is2K ? 460 : 311);
        state.doughnutChart = {
          chartOptions: {
            ...state.doughnutChart.chartOptions,
            mappers,
            labels: legends,
            legend,
          },
          series,
        };
        state.doughnutChart.chartOptions.plotOptions.pie.donut.labels.name.formatter =
          () =>
            i18n.global.t(
              facilityType === "SAUNA"
                ? "congestion.totalInfo"
                : "congestion.totalInfoRoom"
            );
      }
    };

    onMounted(() => {
      if (
        refreshData.value &&
        secondRefresh.value &&
        secondRefresh.value * 1 > 29
      ) {
        intervalRefreshData.value = setInterval(() => {
          getCongestionSituation();
        }, secondRefresh.value * 1000);
      } else {
        clearInterval(intervalRefreshData.value);
      }
      getCongestionSituation();
      window.addEventListener("resize", handleResize);
    });

    onUnmounted(() => {
      window.removeEventListener("resize", handleResize);
      if (resizeTimeout) {
        clearTimeout(resizeTimeout);
      }
    });

    watch(
      () => props.facilityId,
      async () => {
        facilityInfo.value = await getFacilitySelected(props.facilityId);
        await getCongestionSituation();
      },
      { immediate: true }
    );

    watch(
      () => facilityInfo.value?.type,
      (newType, oldType) => {
        if (!oldType) return; // skip first init
        if (newType !== oldType) {
          state.filterType = "all"; // reset filter
        }
      }
    );

    watch(
      () => [state.filterType],
      () => {
        getCongestionSituation();
      }
    );

    watch(
      route,
      () => {
        clearInterval(intervalRefreshData.value);
      },
      { deep: true }
    );

    watch(
      () => props.fullscreen,
      (on) => {
        const defH = state.hasValue ? (props.is2K ? 478 : 320) : (props.is2K ? 435 : 280);
        state.chartHeight = on ? defH + 60 : defH;
        state.fixedHeight = on ? (props.is2K ? 460 : 310) + 60 : (props.is2K ? 460 : 310);
      }
    );

    watch(
      () => props.is2K,
      (on) => {
        const defH = state.hasValue ? (on ? 478 : 320) : (on ? 435 : 280);
        state.chartHeight = props.fullscreen ? defH + 60 : defH;
        state.fixedHeight = props.fullscreen ? (on ? 460 : 310) + 60 : (on ? 460 : 310);
      }
    );

    watch(refreshData, (on) => {
      if (on) {
        if (secondRefresh.value && secondRefresh.value * 1 > 29) {
          intervalRefreshData.value = setInterval(() => {
            getCongestionSituation();
          }, secondRefresh.value * 1000);
        }
      } else {
        clearInterval(intervalRefreshData.value);
      }
    });

    const buttonConfigs = computed(() => {
      if (facilityInfo.value?.type === "SAUNA") {
        return [
          { key: "saunaMen", label: i18n.global.t("congestion.saunaMen") },
          { key: "saunaWomen", label: i18n.global.t("congestion.saunaWomen") },
        ];
      }
      return [
        { key: "room1", label: i18n.global.t("t-lounge-1") },
        { key: "room2", label: i18n.global.t("t-lounge-2") },
      ];
    });

    return {
      state,
      facilityInfo,
      getVariant,
      change,
      buttonConfigs,
      apexchartRef,
    };
  },
};
</script>

<style scoped lang="scss">
.congestion-card {
  & .card-header {
    border-top-left-radius: 6px;
    border-top-right-radius: 6px;
  }

  border-radius: 6px;
  margin-bottom: 0;

  & button {
    font-size: 12px !important;
  }

  & .btn-light {
    color: #a1a1a1 !important;
  }
}

.apex-charts:deep(text) {
  white-space: pre-line !important;
  fill: #adb5bd;
}

.apex-charts:deep(span) {
  white-space: pre-line !important;
}

.apex-charts:deep(.apexcharts-datalabel-value) {
  font-size: 16px !important;
  fill: #000 !important;
}

[data-layout-mode="dark"] {
  .apex-charts:deep(.apexcharts-datalabel-value) {
    fill: white !important;
  }
}

.apex-charts:deep(.apexcharts-datalabel-label) {
  color: #a1a1a1 !important;
  fill: #a1a1a1 !important;
  font-size: 9px;
}

.apex-charts:deep(.apexcharts-legend-text) {
  text-align: center;
  font-size: 12px !important;
}
</style>
